import * as CookieConsent from "vanilla-cookieconsent"

const GTM_ID = "GTM-MPQKK7C"
const CONSENT_EVENT = "drive2_consent_update"
const CONSENT_REVISION = 1
let lastConsentState = ""
let gtmLoaded = false

const analyticsCookiePatterns = [
  /^_ga(?:_|$)/,
  /^_gid$/,
  /^_gat(?:_|$)/,
  /^_gcl_(?:au|aw|dc)$/,
  /^_cl(?:ck|sk)$/,
  /^(?:CLID|ANONCHK|MR|MUID|SM)$/,
]

const isDrive2ProductionHost = () => {
  if (typeof window === "undefined") return false

  return window.location.hostname === "drive2.city" || window.location.hostname.endsWith(".drive2.city")
}

const getCookieConfig = () => ({
  name: "drive2_cookie_consent",
  domain: isDrive2ProductionHost() ? ".drive2.city" : "",
  path: "/",
  sameSite: "Lax",
  secure: isDrive2ProductionHost(),
  expiresAfterDays: 180,
})

const getGoogleConsent = analyticsGranted => ({
  analytics_storage: analyticsGranted ? "granted" : "denied",
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
  functionality_storage: "granted",
  security_storage: "granted",
})

const prepareGoogleConsentMode = () => {
  window.dataLayer = window.dataLayer || []
  window.gtag = window.gtag || function gtag() {
    window.dataLayer.push(arguments)
  }

  window.gtag("consent", "default", {
    ...getGoogleConsent(false),
    wait_for_update: 500,
  })
}

const clearAnalyticsCookies = () => {
  document.cookie.split(";").forEach(cookie => {
    const name = cookie.trim().split("=")[0]
    if (!analyticsCookiePatterns.some(pattern => pattern.test(name))) return

    const hostnameParts = window.location.hostname.split(".")
    const domains = [window.location.hostname]
    for (let index = 1; index < hostnameParts.length - 1; index += 1) {
      domains.push(`.${hostnameParts.slice(index).join(".")}`)
    }

    domains.forEach(domain => {
      document.cookie = `${name}=; Max-Age=0; path=/; domain=${domain}; SameSite=Lax`
    })
  })
}

const getCurrentConsent = () => ({
  analytics: CookieConsent.acceptedCategory("analytics"),
  externalMedia: CookieConsent.acceptedCategory("external_media"),
})

const synchronizeConsent = () => {
  const { analytics, externalMedia } = getCurrentConsent()
  const state = `${analytics}:${externalMedia}`
  if (state === lastConsentState) return

  lastConsentState = state
  window.gtag("consent", "update", getGoogleConsent(analytics))
  window.dataLayer.push({
    event: CONSENT_EVENT,
    drive2_analytics_consent: analytics,
    drive2_external_media_consent: externalMedia,
  })

  if (!analytics) clearAnalyticsCookies()

  window.dispatchEvent(new CustomEvent("drive2:consent-change", {
    detail: { analytics, externalMedia },
  }))
}

const loadGoogleTagManager = () => {
  if (gtmLoaded || document.getElementById("d2c-gtm")) return

  gtmLoaded = true
  window.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" })

  const script = document.createElement("script")
  script.id = "d2c-gtm"
  script.async = true
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`
  document.head.appendChild(script)
}

export const initializeCookieConsent = async () => {
  if (typeof window === "undefined" || window.__d2cConsentInitialized) return
  window.__d2cConsentInitialized = true

  prepareGoogleConsentMode()

  await CookieConsent.run({
    revision: CONSENT_REVISION,
    cookie: getCookieConfig(),
    categories: {
      necessary: { enabled: true, readOnly: true },
      analytics: {
        autoClear: { cookies: analyticsCookiePatterns.map(name => ({ name })) },
      },
      external_media: {},
    },
    guiOptions: {
      consentModal: { layout: "box", position: "bottom right", equalWeightButtons: true },
      preferencesModal: { layout: "box", position: "right", equalWeightButtons: true },
    },
    language: {
      default: "en",
      translations: {
        en: {
          consentModal: {
            title: "Your privacy matters",
            description: 'We use necessary technologies to operate this website and application. With your permission, we also use analytics to understand how our services are used and external media to display third-party content. You can accept all, reject optional services, or manage your preferences. <a href="/privacy-policy">Privacy Policy</a>',
            acceptAllBtn: "Accept all",
            acceptNecessaryBtn: "Reject all",
            showPreferencesBtn: "Manage preferences",
          },
          preferencesModal: {
            title: "Cookie preferences",
            acceptAllBtn: "Accept all",
            acceptNecessaryBtn: "Reject all",
            savePreferencesBtn: "Save preferences",
            closeIconLabel: "Close",
            sections: [
              {
                title: "How we use technologies",
                description: 'You can change optional preferences at any time. See our <a href="/privacy-policy">Privacy Policy</a>.',
              },
              {
                title: "Necessary",
                description: "These technologies are required for authentication, security, core application features, route planning, maps and payment processing. They cannot be disabled.",
                linkedCategory: "necessary",
              },
              {
                title: "Analytics",
                description: "Analytics helps us understand how visitors use our website and application. This category includes Google Analytics and Microsoft Clarity.",
                linkedCategory: "analytics",
              },
              {
                title: "External media",
                description: "External media allows content from third-party platforms, such as YouTube and social-network embeds, to be displayed. Loading this content may transfer data to the external provider.",
                linkedCategory: "external_media",
              },
            ],
          },
        },
      },
    },
    onFirstConsent: synchronizeConsent,
    onConsent: synchronizeConsent,
    onChange: synchronizeConsent,
  })

  loadGoogleTagManager()
}

export const hasExternalMediaConsent = () => {
  if (typeof window === "undefined") return false
  return CookieConsent.acceptedCategory("external_media")
}

export const acceptExternalMedia = () => CookieConsent.acceptCategory("external_media")
export const showCookiePreferences = () => CookieConsent.showPreferences()
